import { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Checkbox } from '@components/base/inputs';
import { Breadcrumb } from '@components/base/navigation';
import { HStack, VStack, Card } from '@components/base/layout';
import { Alert, Spinner, useToast } from '@components/base/feedback';
import { tenantsService } from '../../api/tenantsService';

// ---- JSON Syntax Highlighting ----

function highlightJson(text: string): string {
  if (!text) return '';

  // Escape HTML entities first
  const escaped = text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

  // Tokenize and colorize JSON
  return escaped.replaceAll(
    /("(?:\\.|[^"\\])*")\s*:/g, // keys (strings followed by colon)
    '<span style="color:#7dd3fc">$1</span>:',
  ).replaceAll(
    /:\s*("(?:\\.|[^"\\])*")/g, // string values (strings after colon)
    ': <span style="color:#86efac">$1</span>',
  ).replaceAll(
    // Remaining standalone strings (e.g. in arrays)
    /(?<=[[,\n]\s*)("(?:\\.|[^"\\])*")(?=\s*[,\]\n])/g,
    '<span style="color:#86efac">$1</span>',
  ).replaceAll(
    /\b(true|false)\b/g,
    '<span style="color:#fbbf24">$1</span>',
  ).replaceAll(
    /\bnull\b/g,
    '<span style="color:#a78bfa">null</span>',
  ).replaceAll(
    /(?<=:\s*)(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)(?=\s*[,}\]\n])/g,
    '<span style="color:#f9a8d4">$1</span>',
  );
}

// Shared editor styles — must be identical on both layers for alignment
const editorFont = {
  fontFamily: 'var(--font-mono, "Fira Code"), monospace',
  fontSize: '13px',
  lineHeight: '24px',
  tabSize: 2,
  padding: '16px',
  border: 'none',
  whiteSpace: 'pre' as const,
  wordWrap: 'normal' as const,
  overflowWrap: 'normal' as const,
};

export function TenantModuleConfigPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { slug, moduleSlug } = useParams<{
    slug: string;
    moduleSlug: string;
  }>();

  // ---- Data Fetching ----

  const {
    data: modulesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'tenants', slug, 'modules'],
    queryFn: () => tenantsService.listModules(slug!),
    enabled: slug !== undefined,
  });

  const tenantModule = useMemo(
    () => modulesData?.data?.find((m) => m.slug === moduleSlug) ?? null,
    [modulesData, moduleSlug],
  );

  // ---- Form State ----

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [configText, setConfigText] = useState<string | null>(null);

  // Derive initial values from the fetched module
  const effectiveEnabled =
    enabled ?? (tenantModule?.is_enabled ?? true);
  const effectiveConfigText =
    configText ?? (tenantModule?.config
      ? JSON.stringify(tenantModule.config, null, 2)
      : '');

  // ---- JSON Validation ----

  const jsonError = useMemo(() => {
    if (!effectiveConfigText.trim()) return null;
    try {
      JSON.parse(effectiveConfigText);
      return null;
    } catch (e) {
      /* v8 ignore start */
      return e instanceof Error ? e.message : 'Invalid JSON';
      /* v8 ignore stop */
    }
  }, [effectiveConfigText]);

  const isValidJson = jsonError === null;

  // ---- Highlighted HTML (memoised) ----

  const highlightedHtml = useMemo(
    () => highlightJson(effectiveConfigText),
    [effectiveConfigText],
  );

  // ---- Scroll sync ----

  const preRef = useRef<HTMLPreElement>(null);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLTextAreaElement>) => {
      /* v8 ignore start */
      if (preRef.current) {
        preRef.current.scrollTop = e.currentTarget.scrollTop;
        preRef.current.scrollLeft = e.currentTarget.scrollLeft;
      }
      /* v8 ignore stop */
    },
    [],
  );

  // ---- Mutation ----

  const updateMutation = useMutation({
    mutationFn: (data: {
      is_enabled: boolean;
      config: Record<string, unknown> | null;
    }) => tenantsService.updateModule(slug!, moduleSlug!, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', slug, 'modules'],
      });
      toast({ variant: 'success', title: 'Module updated successfully.' });
      navigate(`/admin/tenants/${slug}`);
    },
  });

  // ---- Handlers ----

  const handleSave = () => {
    let parsedConfig: Record<string, unknown> | null = null;
    if (effectiveConfigText.trim()) {
      try {
        parsedConfig = JSON.parse(effectiveConfigText) as Record<
          string,
          unknown
        >;
      /* v8 ignore start */
      } catch {
        return;
      }
      /* v8 ignore stop */
    }
    updateMutation.mutate({
      is_enabled: effectiveEnabled,
      config: parsedConfig,
    });
  };

  const handleCancel = () => {
    navigate(`/admin/tenants/${slug}`);
  };

  // ---- Render: Loading ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-16">
        <Spinner size="lg" />
        <Text color="muted">Loading module configuration...</Text>
      </VStack>
    );
  }

  // ---- Render: Error ----

  if (error) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/tenants">Tenants</Breadcrumb.Item>
          <Breadcrumb.Item href={`/admin/tenants/${slug}`}>
            {slug}
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Error</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load module">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred while loading module data.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Render: Module Not Found ----

  if (!tenantModule) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/tenants">Tenants</Breadcrumb.Item>
          <Breadcrumb.Item href={`/admin/tenants/${slug}`}>
            {slug}
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Not Found</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Module not found">
          The module &quot;{moduleSlug}&quot; was not found for this tenant.
        </Alert>
      </VStack>
    );
  }

  // ---- Render: Main ----

  return (
    <VStack gap={6}>
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/tenants">Tenants</Breadcrumb.Item>
        <Breadcrumb.Item href={`/admin/tenants/${slug}`}>
          {slug}
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{tenantModule.name}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Module Config Card */}
      <Card>
        <Card.Header title={tenantModule.name} />
        <Card.Content>
          <VStack gap={6}>
            {/* Enabled Toggle */}
            <Checkbox
              label="Enabled"
              checked={effectiveEnabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />

            {/* Configuration JSON */}
            <VStack gap={2}>
              <Text as="label" size="sm" weight="medium">
                Configuration (JSON)
              </Text>
              <div className="rounded-lg overflow-hidden shadow-sm" style={{ border: '1px solid #1e293b' }}>
                {/* Editor header bar */}
                <div
                  className="flex items-center justify-between px-4 py-2"
                  style={{ background: '#1e293b' }}
                >
                  <span
                    style={{ fontFamily: editorFont.fontFamily, fontSize: '12px', color: '#94a3b8' }}
                  >
                    config.json
                  </span>
                  {effectiveConfigText.trim() && (
                    <span style={{ fontSize: '12px', color: jsonError ? '#ef4444' : '#22c55e' }}>
                      {jsonError ? 'Invalid' : 'Valid'}
                    </span>
                  )}
                </div>

                {/* Editor area: highlighted pre behind transparent textarea */}
                <div
                  className="relative"
                  style={{ background: '#0f172a', minHeight: '400px' }}
                >
                  {/* Syntax-highlighted layer */}
                  <pre
                    ref={preRef}
                    aria-hidden="true"
                    className="absolute inset-0 m-0 overflow-hidden pointer-events-none"
                    style={{
                      ...editorFont,
                      color: '#cbd5e1',
                      background: 'transparent',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightedHtml + '\n',
                    }}
                  />

                  {/* Editable textarea (transparent text, visible caret) */}
                  <textarea
                    className="relative w-full m-0 resize-y bg-transparent"
                    style={{
                      ...editorFont,
                      color: 'transparent',
                      caretColor: '#60a5fa',
                      outline: 'none',
                      minHeight: '400px',
                    }}
                    rows={24}
                    value={effectiveConfigText}
                    onChange={(e) => setConfigText(e.target.value)}
                    onScroll={handleScroll}
                    placeholder={'{\n  "key": "value"\n}'}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                  />
                </div>
              </div>
              {jsonError && (
                <Text size="xs" color="error">
                  {jsonError}
                </Text>
              )}
            </VStack>

            {/* Mutation Error */}
            {updateMutation.isError && (
              <Alert variant="error" title="Failed to update module">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}

            {/* Actions */}
            <HStack gap={3} justify="end">
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={updateMutation.isPending}
                disabled={!isValidJson}
              >
                Save Changes
              </Button>
            </HStack>
          </VStack>
        </Card.Content>
      </Card>
    </VStack>
  );
}
